import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import "../HomeScreen.css";
import "./Tours.css";
import Navbar from "../Navbar";
import Footer from "../Footer";

// ══════════════════════════════════════════════
// LAZY IMAGE (shows gif loader until image is fetched)
// ══════════════════════════════════════════════
function LazyImage({ src, alt, className, boxClassName = "tour-lazy-img-box", gifClassName = "tour-lazy-img-gif" }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);

    // If image is already in browser cache, it can finish loading before
    // React attaches the onLoad listener below — onLoad never fires in
    // that case, so the gif stays stuck. Manually check `.complete` too.
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <div className={boxClassName}>
      {!loaded && (
        <img
          src="/assets/images/globegif.gif"
          alt="loading"
          className={gifClassName}
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    </div>
  );
}

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedTour, setSelectedTour] = useState(null); // New state for Tour Modal
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    const fetchToursAndPackages = async () => {
      try {
        const toursQ = query(
          collection(db, "tours"),
          where("isActive", "==", true),
          orderBy("createdAt", "desc")
        );
        const toursSnap = await getDocs(toursQ);

        const toursWithPackages = await Promise.all(
          toursSnap.docs.map(async (tourDoc) => {
            const tour = { id: tourDoc.id, ...tourDoc.data() };

            const pkgsQ = query(
              collection(db, "tours", tourDoc.id, "packages"),
              where("isActive", "==", true),
              orderBy("createdAt", "desc")
            );
            const pkgsSnap = await getDocs(pkgsQ);
            tour.packages = pkgsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            return tour;
          })
        );

        setTours(toursWithPackages);
      } catch (err) {
        console.error("Error fetching tours: ", err);
        setError(err.message || "Failed to load tours.");
      } finally {
        setLoading(false);
      }
    };

    fetchToursAndPackages();
  }, []);

  // Filter Logic
  const filteredTours = tours.filter((tour) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "international") return tour.isInternational === true;
    if (activeFilter === "domestic") return tour.isInternational !== true; 
    return true;
  });

  return (
    <div className="home-container">
      <Navbar />

      {/* Hero */}
      <section className="hero-section tours-hero">
        <div className="hero-overlay">
          <h1>
            Exclusive <span style={{
              background: "linear-gradient(135deg, #ff8c00, #ff5500)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent"
            }}>Tours</span>
          </h1>
          <p>Handcrafted itineraries for the ultimate travel experience.</p>
        </div>
      </section>

      {/* Main Section */}
      <section className="tours-section">
        <h2>All Tour Packages</h2>
        <p className="tours-subtitle">
          Browse our complete collection of premium tour packages curated by experts.
        </p>

        {/* Filter Buttons */}
        <div className="tours-filter-container">
          <button className={`tour-filter-btn ${activeFilter === 'all' ? 'active' : ''}`} onClick={() => setActiveFilter('all')}>All Tours</button>
          <button className={`tour-filter-btn ${activeFilter === 'domestic' ? 'active' : ''}`} onClick={() => setActiveFilter('domestic')}>🇮🇳 Domestic</button>
          <button className={`tour-filter-btn ${activeFilter === 'international' ? 'active' : ''}`} onClick={() => setActiveFilter('international')}>🌍 International</button>
        </div>

        {/* Loading Skeletons */}
        {loading && (
          <div className="tours-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div className="tour-card-skeleton shimmer" key={i}>
                <div className="skel-img" />
                <div className="skel-body">
                  <div className="skel-line w-80" />
                  <div className="skel-line w-60" />
                  <div className="skel-line w-40" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="tours-error">
            <span>⚠️</span>
            <p>{error}</p>
            <small>Check browser console for details.</small>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredTours.length === 0 && (
          <div className="tours-empty">
            <div className="empty-icon">🗺️</div>
            <h3>No Tours Available</h3>
            <p>{activeFilter === 'all' ? 'Check back soon — exciting packages are being added!' : `No ${activeFilter} tours available at the moment.`}</p>
          </div>
        )}

        {/* ✅ NEW: Tours Grid (Cards) */}
        {!loading && !error && filteredTours.length > 0 && (
          <div className="tours-grid">
            {filteredTours.map((tour) => {
              const mainImage = tour.imageUrls?.[0] || "";
              const isIntl = tour.isInternational === true;

              return (
                <div className="tour-card" key={tour.id} onClick={() => setSelectedTour(tour)}>
                  {/* Card Image */}
                  <div className="tour-card-img-wrapper">
                    {mainImage ? (
                      <LazyImage src={mainImage} alt={tour.tourName} className="tour-card-img" />
                    ) : (
                      <div className="tour-card-img-placeholder">🌏</div>
                    )}
                    {/* Domestic / International Badge */}
                    <span className={`tour-type-badge ${isIntl ? 'badge-intl' : 'badge-dom'}`}>
                      {isIntl ? '🌍 International' : '🇮🇳 Domestic'}
                    </span>
                  </div>

                  {/* Card Body */}
                  <div className="tour-card-body">
                    <h3 className="tour-card-title">{tour.tourName}</h3>
                    <p className="tour-card-desc">
                      {tour.description 
                        ? (tour.description.length > 80 ? tour.description.slice(0, 80) + '...' : tour.description)
                        : 'Explore amazing destinations with our curated packages.'}
                    </p>
                    
                    <div className="tour-card-footer">
                      <span className="tour-pkg-count">
                        {tour.packages.length} {tour.packages.length === 1 ? 'Package' : 'Packages'}
                      </span>
                      <button className="tour-view-btn">
                        Explore →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ✅ NEW: Tour Detail Modal (Shows Packages inside) */}
      {selectedTour && (
        <div className="pkg-modal-overlay" onClick={() => setSelectedTour(null)}>
          <div className="tour-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedTour(null)}>✕</button>
            
            {/* Modal Header */}
            <div className="tour-modal-header">
              {selectedTour.imageUrls?.[0] && (
                <LazyImage
                  src={selectedTour.imageUrls[0]}
                  alt={selectedTour.tourName}
                  className="tour-modal-bg-img"
                  boxClassName="tour-modal-lazy-img-box"
                  gifClassName="tour-modal-lazy-img-gif"
                />
              )}
              <div className="tour-modal-header-overlay">
                <span className={`tour-type-badge ${selectedTour.isInternational ? 'badge-intl' : 'badge-dom'}`}>
                  {selectedTour.isInternational ? '🌍 International' : '🇮🇳 Domestic'}
                </span>
                <h2>{selectedTour.tourName}</h2>
                <p>{selectedTour.description}</p>
              </div>
            </div>

            {/* Modal Body (Packages) */}
            <div className="tour-modal-body">
              <h3 className="packages-heading">Available Packages ({selectedTour.packages.length})</h3>
              
              {selectedTour.packages.length === 0 ? (
                <div className="no-packages">
                  <span>📦</span>
                  <p>No active packages for this tour yet.</p>
                </div>
              ) : (
                <div className="pkg-grid">
                  {selectedTour.packages.map((pkg) => (
                    <div className="pkg-card" key={pkg.id} onClick={() => setSelectedPackage({ ...pkg, tourName: selectedTour.tourName })}>
                      <div className="pkg-price-badge">
                        {pkg.discountPrice ? (
                          <>
                            <span className="pkg-discount-price">₹{pkg.discountPrice.toLocaleString("en-IN")}</span>
                            <span className="pkg-original-price">₹{pkg.price?.toLocaleString("en-IN")}</span>
                          </>
                        ) : (
                          <span className="pkg-price">{pkg.price ? `₹${pkg.price.toLocaleString("en-IN")}` : "Price on request"}</span>
                        )}
                      </div>
                      <div className="pkg-card-body">
                        <h4 className="pkg-name">{pkg.packageName}</h4>
                        <div className="pkg-chips">
                          {pkg.destination && <span className="pkg-chip">📍 {pkg.destination}</span>}
                          {pkg.duration && <span className="pkg-chip">🕐 {pkg.duration}</span>}
                        </div>
                        {pkg.description && (
                          <p className="pkg-desc">
                            {pkg.description.length > 70 ? pkg.description.slice(0, 70) + "…" : pkg.description}
                          </p>
                        )}
                        <button className="pkg-view-btn">View Details →</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Package Detail Modal (Unchanged from before) */}
      {selectedPackage && (
        <div className="pkg-modal-overlay" onClick={() => setSelectedPackage(null)}>
          <div className="pkg-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPackage(null)}>✕</button>
            <div className="pkg-modal-header">
              <p className="pkg-modal-tour-name">{selectedPackage.tourName}</p>
              <h2 className="pkg-modal-title">{selectedPackage.packageName}</h2>
              <div className="pkg-modal-chips">
                {selectedPackage.destination && <span className="pkg-chip">📍 {selectedPackage.destination}</span>}
                {selectedPackage.duration && <span className="pkg-chip">🕐 {selectedPackage.duration}</span>}
              </div>
              <div className="pkg-modal-price">
                {selectedPackage.discountPrice ? (
                  <>
                    <span className="modal-discount">₹{selectedPackage.discountPrice.toLocaleString("en-IN")}</span>
                    <span className="modal-original">₹{selectedPackage.price?.toLocaleString("en-IN")}</span>
                    <span className="modal-saving">Save ₹{(selectedPackage.price - selectedPackage.discountPrice).toLocaleString("en-IN")}</span>
                  </>
                ) : (
                  <span className="modal-discount">{selectedPackage.price ? `₹${selectedPackage.price.toLocaleString("en-IN")}` : "Price on request"}</span>
                )}
              </div>
            </div>
            <div className="pkg-modal-body">
              {selectedPackage.description && (<div className="pkg-modal-section"><h4>📋 Overview</h4><p>{selectedPackage.description}</p></div>)}
              {selectedPackage.itinerary && (<div className="pkg-modal-section"><h4>🗓️ Itinerary</h4><p className="pre-wrap">{selectedPackage.itinerary}</p></div>)}
              <div className="pkg-modal-inc-exc">
                {selectedPackage.inclusions && (<div className="inc-box"><h4>✅ Inclusions</h4><p className="pre-wrap">{selectedPackage.inclusions}</p></div>)}
                {selectedPackage.exclusions && (<div className="exc-box"><h4>❌ Exclusions</h4><p className="pre-wrap">{selectedPackage.exclusions}</p></div>)}
              </div>
              <div className="pkg-modal-actions">
                <button className="whatsapp-btn" onClick={() => window.open(`https://wa.me/919096785868?text=Hi! I'm interested in the "${selectedPackage.packageName}" package from "${selectedPackage.tourName}" tour.`, "_blank")}>
                  <i className="fab fa-whatsapp"></i> Enquire on WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp CTA */}
      <section className="whatsapp-section" style={{ margin: "0 5% 3rem" }}>
        <h2>Need Help Choosing a Tour?</h2>
        <p>Talk to our travel experts and get a personalized recommendation.</p>
        <button className="whatsapp-btn" onClick={() => window.open("https://wa.me/919096785868", "_blank")}>
          <i className="fab fa-whatsapp"></i> Chat on WhatsApp
        </button>
      </section>

      <Footer />
    </div>
  );
};

export default Tours;